import { Track, TelemetryState, DspState } from '../types';
import { sanitizeTrackStream } from './monochromeApi';

type AudioListener = () => void;

class AudioEngine {
  private audio: HTMLAudioElement;
  private listeners: Set<AudioListener> = new Set();
  private audioUnlocked: boolean = false;
  private lastTickTs: number = performance.now();

  public dsp: DspState = {
    bassDb: 0,
    trebleDb: 0,
    balance: 0,
  };

  public currentTrack: Track | null = null;
  public queue: Track[] = [];
  public isPlaying: boolean = false;
  public currentTime: number = 0;
  public duration: number = 0;
  public volume: number = 0.85;
  public isShuffle: boolean = false;
  public isRepeat: boolean = false;
  public autoPlayNext: boolean = true;
  public telemetry: TelemetryState = {
    rmsLeft: 0,
    rmsRight: 0,
    peakDbL: -99.9,
    peakDbR: -99.9,
    dacLocked: true,
    bitPerfect: true,
    sampleRate: '--',
    bitDepth: '--',
    bufferHealth: 100,
  };

  private telemetryTimer: number | null = null;

  constructor() {
    this.audio = new Audio();
    this.audio.preload = 'metadata';
    this.audio.crossOrigin = 'anonymous';
    this.audio.volume = this.volume;

    this.setupAudioListeners();
    this.startTelemetryLoop();
  }

  private buildStreamSrc(track: Track, qualityOverride?: string): string {
    const clean = sanitizeTrackStream(track);
    const rawUrl = clean.streamUrl || `/api/stream/${encodeURIComponent(clean.id)}`;
    const [basePath, existingQuery = ''] = rawUrl.split('?');
    const params = new URLSearchParams(existingQuery);
    if (!params.get('title') && clean.title) params.set('title', clean.title);
    if (!params.get('artist') && clean.artist) params.set('artist', clean.artist);
    if (qualityOverride) {
      params.set('quality', qualityOverride);
    } else if (!params.get('quality')) {
      params.set('quality', '7');
    }
    const qs = params.toString();
    return qs ? `${basePath}?${qs}` : basePath;
  }

  private setupAudioListeners() {
    this.audio.addEventListener('play', () => {
      this.isPlaying = true;
      this.audioUnlocked = true;
      this.notify();
    });

    this.audio.addEventListener('pause', () => {
      this.isPlaying = false;
      this.notify();
    });

    this.audio.addEventListener('timeupdate', () => {
      if (!this.audio.paused && !isNaN(this.audio.currentTime)) {
        this.currentTime = this.audio.currentTime;
        this.notify();
      }
    });

    this.audio.addEventListener('loadedmetadata', () => {
      if (this.audio.duration && !isNaN(this.audio.duration) && isFinite(this.audio.duration)) {
        this.duration = this.audio.duration;
      }
      this.notify();
    });

    this.audio.addEventListener('ended', () => {
      if (this.isRepeat) {
        this.audio.currentTime = 0;
        this.audio.play().catch(() => {});
      } else if (this.autoPlayNext) {
        this.next();
      } else {
        this.pause();
        this.currentTime = 0;
      }
    });

    this.audio.addEventListener('error', () => {
      if (!this.currentTrack) return;
      const currentSrc = this.audio.src || '';
      if (!currentSrc.includes('quality=5')) {
        const nextQ = currentSrc.includes('quality=6') ? '5' : '6';
        this.audio.src = this.buildStreamSrc(this.currentTrack, nextQ);
        this.audio.load();
        if (this.isPlaying) {
          this.audio.play().catch(() => {
            this.isPlaying = false;
            this.audioUnlocked = false;
            this.notify();
          });
        }
        return;
      }
      console.warn('[AudioEngine] Stream source playback error');
      this.isPlaying = false;
      this.audioUnlocked = false;
      this.notify();
    });
  }

  private startTelemetryLoop() {
    let lastNotify = 0;
    const update = (now: number) => {
      this.lastTickTs = now;

      const isActuallyPlaying = this.isPlaying && !this.audio.paused;

      if (isActuallyPlaying) {
        if (!isNaN(this.audio.currentTime)) {
          this.currentTime = this.audio.currentTime;
        }

        const energy = 0.58 + Math.sin(now * 0.008) * 0.18 + Math.random() * 0.15;
        const volFactor = this.volume;
        const bal = this.dsp.balance;
        const leftPan = bal <= 0 ? 1 : Math.max(0, 1 - bal);
        const rightPan = bal >= 0 ? 1 : Math.max(0, 1 + bal);

        const bassFactor = Math.pow(10, this.dsp.bassDb / 40);
        const trebleFactor = Math.pow(10, this.dsp.trebleDb / 40);
        const toneGain = bassFactor * 0.6 + trebleFactor * 0.4;

        if (volFactor < 0.01) {
          this.telemetry = {
            ...this.telemetry,
            rmsLeft: 0,
            rmsRight: 0,
            peakDbL: -99.9,
            peakDbR: -99.9,
            sampleRate: this.currentTrack ? this.currentTrack.sampleRate : '--',
            bitDepth: this.currentTrack ? this.currentTrack.bitDepth : '--',
          };
        } else {
          const rawL = energy * (0.92 + Math.random() * 0.16) * toneGain * volFactor * leftPan;
          const rawR = energy * (0.90 + Math.random() * 0.18) * toneGain * volFactor * rightPan;

          const l = leftPan === 0 ? 0 : Math.min(0.99, Math.max(0, rawL));
          const r = rightPan === 0 ? 0 : Math.min(0.99, Math.max(0, rawR));

          const dbL = l <= 0.02 ? -99.9 : -Math.max(0.2, (1 - l) * 22);
          const dbR = r <= 0.02 ? -99.9 : -Math.max(0.2, (1 - r) * 22);

          this.telemetry = {
            ...this.telemetry,
            rmsLeft: l,
            rmsRight: r,
            peakDbL: parseFloat(dbL.toFixed(1)),
            peakDbR: parseFloat(dbR.toFixed(1)),
            sampleRate: this.currentTrack ? this.currentTrack.sampleRate : '--',
            bitDepth: this.currentTrack ? this.currentTrack.bitDepth : '--',
          };
        }
      } else {
        this.telemetry = {
          ...this.telemetry,
          rmsLeft: 0,
          rmsRight: 0,
          peakDbL: -99.9,
          peakDbR: -99.9,
        };
      }

      if (now - lastNotify > 65) {
        lastNotify = now;
        this.notify();
      }
      this.telemetryTimer = requestAnimationFrame(update);
    };

    this.telemetryTimer = requestAnimationFrame(update);
  }

  public subscribe(cb: AudioListener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private notify() {
    this.listeners.forEach((cb) => cb());
  }

  public setQueue(tracks: Track[]) {
    this.queue = Array.isArray(tracks) ? tracks.map(sanitizeTrackStream) : [];
    this.notify();
  }

  public restoreQueue(tracks: Track[]) {
    this.queue = Array.isArray(tracks) ? tracks.map(sanitizeTrackStream) : [];
    if (!this.currentTrack && this.queue.length > 0) {
      this.currentTrack = this.queue[0];
      this.duration = this.currentTrack.duration || 0;
      this.currentTime = 0;
    }
    this.notify();
  }

  public syncQueue(tracks: Track[]) {
    const clean = Array.isArray(tracks) ? tracks.map(sanitizeTrackStream) : [];
    if (this.queue.length === clean.length && this.queue.every((t, i) => t.id === clean[i].id)) {
      return;
    }
    this.queue = clean;
    this.notify();
  }

  public clearQueue() {
    this.pause();
    this.queue = [];
    this.currentTrack = null;
    this.currentTime = 0;
    this.duration = 0;
    if (this.audio.src) {
      this.audio.src = '';
    }
    this.notify();
  }

  public loadTrack(track: Track) {
    const sanitized = sanitizeTrackStream(track);
    this.currentTrack = sanitized;
    this.currentTime = 0;
    this.duration = sanitized.duration || 0;
    this.isPlaying = false;
    this.audioUnlocked = false;
    this.audio.src = this.buildStreamSrc(sanitized);
    this.audio.preload = 'metadata';
    this.audio.load();
    this.notify();
  }

  public unlock() {
    if (this.audioUnlocked) return;
    try {
      this.audio.load();
    } catch {}
  }

  public async play(track?: Track) {
    const target = track || this.currentTrack || (this.queue.length > 0 ? this.queue[0] : null);
    if (!target) return;

    const sanitized = sanitizeTrackStream(target);

    // If track is not in queue, add it to queue so played tracks accumulate
    if (!this.queue.some((t) => t.id === sanitized.id)) {
      this.queue = [...this.queue, sanitized];
    }

    const isNewTrack = !this.currentTrack || sanitized.id !== this.currentTrack.id;
    if (isNewTrack || !this.audio.src || this.audio.src.includes('itunes.apple.com') || this.audio.src.includes('AudioPreview')) {
      this.currentTrack = sanitized;
      this.currentTime = 0;
      this.duration = sanitized.duration;
      this.audio.src = this.buildStreamSrc(sanitized);
      this.audio.load();
    }

    try {
      await this.audio.play();
      this.isPlaying = true;
      this.audioUnlocked = true;
    } catch (err) {
      console.warn('[AudioEngine] Playback failed or was blocked by browser policy:', err);
      this.isPlaying = false;
      this.audioUnlocked = false;
    }
    this.notify();
  }

  public pause() {
    this.audio.pause();
    this.isPlaying = false;
    this.notify();
  }

  public togglePlay() {
    if (!this.currentTrack && this.queue.length === 0) return;
    if (this.isPlaying && !this.audio.paused) {
      this.pause();
    } else {
      this.play();
    }
  }

  public seek(seconds: number) {
    const target = Math.max(0, Math.min(seconds, this.duration));
    this.currentTime = target;
    try {
      if (isFinite(target)) {
        this.audio.currentTime = target;
      }
    } catch {
      // ignore if metadata not loaded
    }
    this.notify();
  }

  public setVolume(vol: number) {
    this.volume = Math.max(0, Math.min(1, vol));
    this.audio.volume = this.volume;
    this.notify();
  }

  public setBass(db: number) {
    this.dsp.bassDb = Math.max(-12, Math.min(12, db));
    this.notify();
  }

  public setTreble(db: number) {
    this.dsp.trebleDb = Math.max(-12, Math.min(12, db));
    this.notify();
  }

  public setBalance(pan: number) {
    this.dsp.balance = Math.max(-1, Math.min(1, pan));
    this.notify();
  }

  public resetKnob(key: 'bass' | 'treble' | 'balance') {
    if (key === 'bass') this.setBass(0);
    else if (key === 'treble') this.setTreble(0);
    else if (key === 'balance') this.setBalance(0);
  }


  public next() {
    if (this.queue.length === 0) return;
    const currentId = this.currentTrack?.id;
    const currIdx = currentId ? this.queue.findIndex((t) => t.id === currentId) : -1;
    let nextIdx = currIdx === -1 ? 0 : (currIdx + 1) % this.queue.length;
    if (this.isShuffle) {
      nextIdx = Math.floor(Math.random() * this.queue.length);
    }
    this.play(this.queue[nextIdx]);
  }

  public prev() {
    if (this.queue.length === 0) return;
    if (this.currentTime > 5) {
      this.seek(0);
      return;
    }
    const currentId = this.currentTrack?.id;
    const currIdx = currentId ? this.queue.findIndex((t) => t.id === currentId) : -1;
    const prevIdx = currIdx === -1 ? 0 : (currIdx - 1 + this.queue.length) % this.queue.length;
    this.play(this.queue[prevIdx]);
  }

  public toggleShuffle() {
    this.isShuffle = !this.isShuffle;
    this.notify();
  }

  public toggleRepeat() {
    this.isRepeat = !this.isRepeat;
    this.notify();
  }
}

export const audioEngine = new AudioEngine();
